/* ===========================================================
   Sessão do app — duas camadas:

   1) IMPERSONAÇÃO do operador (demo/admin): quando o painel admin
      "entra como" um lojista, a sessão aponta o tenant ativo do
      client (setActiveTenant) e todas as queries re-escopam sozinhas.
      Persistida em localStorage `lucraa_session_v1`.

   2) SESSÃO REAL do lojista (M1.3, modo http): GET /api/v1/session
      no mount lê o cookie HttpOnly `lucraa_session` setado pelo
      apps/api na confirmação do OTP — 401 → anônimo (guard no
      AppLayout manda para /login). No modo mock não há rede: a
      sessão é SINTETIZADA dos knobs da demo (DemoProvider) com o
      tenant demo, então a demo offline segue sempre "autenticada".

   Em produção a impersonação também migra para a sessão/token:
   o operador assume um token escopado ao lojista, com trilha de
   auditoria (ver ADR 16 em decisions.md).
   =========================================================== */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { setActiveTenant } from "../api/client";
import { API_MODE } from "../api/mode";
import {
  DEMO_TENANT,
  DEMO_LEAD_TENANT,
  buildDemoPersonaEntry,
  getStoreData,
  setDemoPersona,
} from "../mocks/tenants";
import type { SessaoUsuario, TenantRef } from "../api/types";
import { readPersistedPersona, useDemo } from "./DemoProvider";

export interface Impersonation {
  tenant: TenantRef;
  /** Nome da loja, para a barra de impersonação. */
  displayName: string;
}

export type SessaoStatus = "loading" | "anon" | "authed";

interface SessaoState {
  status: SessaoStatus;
  sessao: SessaoUsuario | null;
}

interface SessionContextValue {
  impersonation: Impersonation | null;
  startImpersonation: (tenant: TenantRef, displayName: string) => void;
  stopImpersonation: () => void;
  /** Estado da sessão real (modo http). No mock fica "authed" e o
   *  useSessao sintetiza a SessaoUsuario dos knobs da demo. */
  sessaoHttp: SessaoState;
  refreshSessao: () => Promise<void>;
  sair: () => Promise<void>;
}

const LS_KEY = "lucraa_session_v1";

function loadInitial(): Impersonation | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.tenant?.accountId && parsed?.tenant?.storeId && parsed?.displayName) {
      return parsed as Impersonation;
    }
  } catch {
    /* estado corrompido → sem impersonação */
  }
  return null;
}

/** Tenant ativo no boot/refresh: a impersonação do operador tem prioridade;
 *  senão, se há persona de demo persistida (mock), registra a vitrine sintética
 *  e usa o tenant do lead; senão, a loja-demo. */
function resolveActiveTenant(imp: Impersonation | null): TenantRef {
  if (imp) return imp.tenant;
  const persona = API_MODE !== "http" ? readPersistedPersona() : null;
  if (persona?.ativa) {
    setDemoPersona(buildDemoPersonaEntry({ loja: persona.loja, sizeFactor: persona.sizeFactor }));
    return DEMO_LEAD_TENANT;
  }
  return DEMO_TENANT;
}

// Sincroniza o tenant ativo do client JÁ no carregamento do módulo — antes de
// qualquer tela renderizar/consultar. Sem isto, um reload com impersonação/persona
// persistida buscaria a loja-demo no primeiro render (efeitos rodam filho→pai).
const INITIAL = loadInitial();
setActiveTenant(resolveActiveTenant(INITIAL));

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [impersonation, setImpersonation] = useState<Impersonation | null>(INITIAL);
  // http começa "loading" (resolve no GET /session do mount); mock já nasce
  // "authed" — a demo pública não tem anônimo nem rede.
  const [sessaoHttp, setSessaoHttp] = useState<SessaoState>(() =>
    API_MODE === "http" ? { status: "loading", sessao: null } : { status: "authed", sessao: null },
  );

  useEffect(() => {
    setActiveTenant(resolveActiveTenant(impersonation));
    try {
      if (impersonation) localStorage.setItem(LS_KEY, JSON.stringify(impersonation));
      else localStorage.removeItem(LS_KEY);
    } catch {
      /* storage indisponível — segue sem persistir */
    }
  }, [impersonation]);

  /** Recarrega a sessão do servidor (após confirmar OTP, p.ex.). 401 → anônimo. */
  const refreshSessao = useCallback(async () => {
    if (API_MODE !== "http") return; // mock: sessão sintetizada, nada a buscar
    try {
      const r = await fetch("/api/v1/session", { credentials: "include" });
      if (!r.ok) {
        setSessaoHttp({ status: "anon", sessao: null });
        return;
      }
      const sessao = (await r.json()) as SessaoUsuario;
      setSessaoHttp({ status: "authed", sessao });
    } catch {
      // servidor fora do ar = sem sessão comprovável → anônimo (guard → /login)
      setSessaoHttp({ status: "anon", sessao: null });
    }
  }, []);

  /** Encerra a sessão no servidor (limpa o cookie) e vira anônimo. */
  const sair = useCallback(async () => {
    if (API_MODE !== "http") return; // mock: sair é só navegação (AppLayout)
    try {
      await fetch("/api/v1/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* rede fora — o estado local zera mesmo assim */
    }
    setSessaoHttp({ status: "anon", sessao: null });
  }, []);

  // GET /session no mount (só http). O setState vive no callback do timeout,
  // não no corpo do efeito — mesmo padrão do countdown do VerificarCodigo.
  useEffect(() => {
    if (API_MODE !== "http") return;
    const t = setTimeout(() => void refreshSessao(), 0);
    return () => clearTimeout(t);
  }, [refreshSessao]);

  const startImpersonation = (tenant: TenantRef, displayName: string) =>
    setImpersonation({ tenant, displayName });
  const stopImpersonation = () => setImpersonation(null);

  return (
    <SessionContext.Provider
      value={{
        impersonation,
        startImpersonation,
        stopImpersonation,
        sessaoHttp,
        refreshSessao,
        sair,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession precisa estar dentro de <SessionProvider>");
  return ctx;
}

/* ---------- sessão do lojista (M1.3) ---------- */
export interface UseSessaoValue {
  status: SessaoStatus;
  sessao: SessaoUsuario | null;
  refreshSessao: () => Promise<void>;
  sair: () => Promise<void>;
}

/** Sessão autenticada do lojista. No modo http vem do apps/api; no mock é
 *  sintetizada dos knobs da demo (DemoProvider) + loja-demo — mesma forma
 *  do contrato (SessaoUsuario), zero rede. Use dentro do DemoProvider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useSessao(): UseSessaoValue {
  const { sessaoHttp, refreshSessao, sair } = useSession();
  const demo = useDemo();

  if (API_MODE === "http") {
    return { status: sessaoHttp.status, sessao: sessaoHttp.sessao, refreshSessao, sair };
  }

  // Mock: sempre autenticado; trial/usuário vêm dos knobs (Shift+D). Com persona
  // ativa (gate /demo), a sessão aponta a vitrine do lead; senão a loja-demo.
  const tenantRef = demo.persona?.ativa ? DEMO_LEAD_TENANT : DEMO_TENANT;
  const loja = getStoreData(tenantRef.storeId);
  const sessao: SessaoUsuario = {
    user: { id: "user_demo", nome: demo.user.nome, email: demo.user.email, role: "owner" },
    tenant: { ...tenantRef, nome: loja.nuvemshop.loja, segmento: loja.nuvemshop.segmento },
    trial: { state: demo.trial, endsAt: null },
    faixaDetectada: loja.faixaDetectada,
    assinatura: null,
  };
  return { status: "authed", sessao, refreshSessao, sair };
}
